import { call, put, takeLatest } from "redux-saga/effects";
import * as actionTypes from "./appActionTypes";
import { Location } from "../../interfaces/app.interface";
import appConfig from "../../appConfig";

interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

const EMPTY_LOCATION: Location = {
  latitude: 0,
  longitude: 0,
  address: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
  googleMapsData: [],
};

// Resolves to null on any failure (denied / unavailable / timeout) instead of throwing.
function getCurrentPositionSafe(): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported by this browser.");
      return resolve(null);
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      (err) => {
        // code 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        // These are all expected runtime conditions, not bugs — warn, don't error.
        console.warn(`Geolocation unavailable (code ${err.code}): ${err.message}`);
        resolve(null);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  });
}

async function getLocationApi(): Promise<Location> {
  const position = await getCurrentPositionSafe();

  // No position → return the empty fallback. App continues normally.
  if (!position) {
    return EMPTY_LOCATION;
  }

  const latitude = position.coords.latitude || 0;
  const longitude = position.coords.longitude || 0;

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${appConfig.GOOGLE_MAPS_API_KEY}`
    );
    const data = await response.json();

    if (data.status !== "OK") {
      console.warn("Geocoding returned non-OK status:", data.status);
      return { ...EMPTY_LOCATION, latitude, longitude };
    }

    const addressComponents: AddressComponent[] =
      data.results[0]?.address_components || [];
    const formattedAddress = data.results[0]?.formatted_address || "";

    const findComponent = (type: string) =>
      addressComponents.find((c) => c.types.includes(type))?.long_name || "";

    return {
      latitude,
      longitude,
      address: formattedAddress,
      city: findComponent("locality"),
      state: findComponent("administrative_area_level_1"),
      country: findComponent("country"),
      postalCode: findComponent("postal_code"),
      googleMapsData: data.results || [],
    };
  } catch (error) {
    // Network / parsing failure on the geocode call — keep coords, drop the rest.
    console.warn("Failed to fetch address info from Maps API:", error);
    return { ...EMPTY_LOCATION, latitude, longitude };
  }
}

function* fetchLocationSaga() {
  try {
    const location: Location = yield call(getLocationApi);
    yield put({ type: actionTypes.FETCH_LOCATION_SUCCESS, payload: location });
  } catch (error: any) {
    yield put({
      type: actionTypes.FETCH_LOCATION_FAILURE,
      payload: error?.message ?? "Unknown error",
    });
  }
}

export default function* appSaga() {
  yield takeLatest(actionTypes.FETCH_LOCATION, fetchLocationSaga);
}